import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RefreshIcon } from '../icons/CorporateIcons';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks } from '../../constants/theme';

interface RefreshButtonProps {
  onClick: () => void | Promise<void>;
  label?: string;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ButtonContainer = styled.button<{
  size: 'small' | 'medium' | 'large';
  variant: 'primary' | 'secondary' | 'ghost';
  isRefreshing: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${props => {
    switch (props.size) {
      case 'small': return Spacing.xs;
      case 'large': return Spacing.md;
      default: return Spacing.sm;
    }
  }}px;
  
  padding: ${props => {
    switch (props.size) {
      case 'small': return `${Spacing.xs}px ${Spacing.sm}px`;
      case 'large': return `${Spacing.md}px ${Spacing.lg}px`;
      default: return `${Spacing.sm}px ${Spacing.md}px`;
    }
  }};
  
  font-family: ${FontStacks.primary};
  font-size: ${props => {
    switch (props.size) {
      case 'small': return FontSizes.sm;
      case 'large': return FontSizes.md;
      default: return FontSizes.sm;
    }
  }}px;
  font-weight: ${FontWeights.medium};
  
  border-radius: ${BorderRadius.sm}px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  border: 1px solid transparent;
  
  /* Variant styles */
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${Colors.primary};
          color: ${Colors.textOnPrimary};
          border-color: ${Colors.primary};
          
          &:hover:not(:disabled) {
            background: ${Colors.primaryDark};
            border-color: ${Colors.primaryDark};
            box-shadow: ${Shadows.sm};
          }
          
          &:active:not(:disabled) {
            transform: translateY(1px);
          }
        `;
      
      case 'secondary':
        return `
          background: ${Colors.surface};
          color: ${Colors.primary};
          border-color: ${Colors.border};
          
          &:hover:not(:disabled) {
            background: ${Colors.surfaceHover};
            border-color: ${Colors.primary};
            box-shadow: ${Shadows.sm};
          }
          
          &:active:not(:disabled) {
            background: ${Colors.surfaceSelected};
          }
        `;
      
      case 'ghost':
      default:
        return `
          background: transparent;
          color: ${Colors.textSecondary};
          
          &:hover:not(:disabled) {
            background: ${Colors.surfaceHover};
            color: ${Colors.textPrimary};
          }
          
          &:active:not(:disabled) {
            background: ${Colors.surfaceSelected};
          }
        `;
    }
  }}
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    color: ${Colors.textTertiary};
  }
  
  /* Focus state for accessibility */
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.primary}30;
  }
`;

const IconWrapper = styled.span<{ isRefreshing: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: ${props => props.isRefreshing ? rotate : 'none'} 1s linear infinite;
`;

const RefreshButton: React.FC<RefreshButtonProps> = ({
  onClick,
  label,
  showLabel = true,
  size = 'medium',
  variant = 'ghost',
  disabled = false,
  className,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleClick = async () => {
    if (disabled || isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      await onClick();
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      // Keep spinning for at least 1 second for visual feedback
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    }
  };

  const buttonLabel = label || t('common.refresh', 'Refresh');
  const iconSize = size === 'small' ? 14 : size === 'large' ? 18 : 16;

  return (
    <ButtonContainer
      onClick={handleClick}
      size={size}
      variant={variant}
      disabled={disabled || isRefreshing}
      isRefreshing={isRefreshing}
      className={className}
      aria-label={ariaLabel || buttonLabel}
      aria-busy={isRefreshing}
    >
      <IconWrapper isRefreshing={isRefreshing}>
        <RefreshIcon size={iconSize} />
      </IconWrapper>
      {showLabel && <span>{buttonLabel}</span>}
    </ButtonContainer>
  );
};

export default RefreshButton;

// Export a preset version for common use cases
export const StandardRefreshButton: React.FC<Omit<RefreshButtonProps, 'size' | 'variant'>> = (props) => (
  <RefreshButton size="medium" variant="ghost" {...props} />
);

export const PrimaryRefreshButton: React.FC<Omit<RefreshButtonProps, 'size' | 'variant'>> = (props) => (
  <RefreshButton size="medium" variant="primary" {...props} />
);

export const IconOnlyRefreshButton: React.FC<Omit<RefreshButtonProps, 'showLabel'>> = (props) => (
  <RefreshButton showLabel={false} {...props} />
);